import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconMail, IconPlus, IconSearch } from "@tabler/icons-react";

import {
  useGetAllTickets,
  useGetTicketEmailCount,
  useSendTicketEmails,
} from "../../api/tickets";
import { useGetEvent } from "../../api/event";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import AttendeeLimitBanner from "../AttendeeLimitBanner";
import { confirmSendTicketEmails } from "../Tickets/sendTicketEmails";
import StatusList from "./StatusList";
import TicketPanel from "./TicketPanel";

const PAGE_SIZE = 50;

/**
 * The tickets as an organizer works them: a list that says where each ticket
 * is in the app, with a row action only when there is something to do, and
 * one ticket at a time in a side panel.
 * The Tickets page keeps the history, import, export and settings.
 */
function TicketsPreview() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const eventId = getCurrentWorkspace(user).eventId;

  const { data: event } = useGetEvent(eventId);
  const { data: ticketEmails } = useGetTicketEmailCount({ eventId });
  const sendTicketEmails = useSendTicketEmails({ eventId });

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  const [openId, setOpenId] = useState(null);

  // Another event starts from its first page with no search.
  useEffect(() => {
    setPage(1);
    setSearch("");
    setOpenId(null);
  }, [eventId]);

  const { data, isPending } = useGetAllTickets({
    eventId,
    page,
    limit: PAGE_SIZE,
    sort: { columnAccessor: "firstName", direction: "asc" },
    ...(debouncedSearch && {
      searchQuery: debouncedSearch,
      searchByName: true,
    }),
  });
  // The open ticket follows the list, so it shows each change at once.
  const openTicket = data?.data?.find((ticket) => ticket.id === openId);

  const pendingEmails = ticketEmails?.pending ?? 0;
  const sendEmails = () =>
    confirmSendTicketEmails({
      pending: pendingEmails,
      withoutEmail: ticketEmails?.withoutEmail ?? 0,
      send: sendTicketEmails.mutate,
    });

  const actions = useMemo(
    () => [
      {
        label: pendingEmails
          ? `Send ticket emails (${pendingEmails})`
          : "Send ticket emails",
        onClick: sendEmails,
        icon: <IconMail />,
        variant: "outline",
        loading: sendTicketEmails.isPending,
      },
      {
        label: "Create",
        onClick: () => navigate("/app/tickets/create"),
        icon: <IconPlus />,
        disabled: event?.isHardBlocked,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      event?.isHardBlocked,
      pendingEmails,
      ticketEmails?.withoutEmail,
      sendTicketEmails.isPending,
    ]
  );
  useSetActionButtons(actions);

  return (
    <Stack gap="md">
      <AttendeeLimitBanner
        ticketsCount={event?.ticketsCount}
        attendeeLimit={event?.attendeeLimit}
        isAttendeeLimitReached={event?.isAttendeeLimitReached}
        isHardBlocked={event?.isHardBlocked}
      />
      <TextInput
        placeholder="Search by name"
        leftSection={<IconSearch size={16} />}
        maw={360}
        value={search}
        onChange={(e) => {
          setSearch(e.currentTarget.value);
          setPage(1);
        }}
      />
      <StatusList
        records={data?.data}
        onOpen={setOpenId}
        tableProps={{
          totalRecords: data?.pagination?.total,
          recordsPerPage: PAGE_SIZE,
          page,
          onPageChange: setPage,
          fetching: isPending,
        }}
      />
      <TicketPanel
        ticket={openTicket}
        eventId={eventId}
        onClose={() => setOpenId(null)}
      />
    </Stack>
  );
}

export default TicketsPreview;
